import type { CustomerType } from "@origin-digital/sales-cart";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Box, Preloader, Stack } from "@origin-digital/ods-core";
import {
  type IFuelTypes,
  type PlanFuelSelection,
  removePlanSolar,
  setPlanFuel,
  setPlanSolar,
  YesNoE,
} from "@origin-digital/sales-common";
import { logger } from "@origin-digital/reporting-client";
import { isErgon, isErgonSolar } from "@origin-digital/sales-common-types-fns";
import {
  EnergyAddressLookup,
  UpliftedEnergyAddressLookup,
  useEnergyAddressLookup,
  NMIResolutionState,
  type ConnectionAddressObject,
} from "@origin-digital/sales-energy-address-lookup";

import { FUEL_DUAL, FUEL_ELEC, FUEL_GAS } from "src/consts";
import { ILookup } from "src/consts/interfaces";
import { CampaignPlans, getCampaignPlans } from "src/utils/campaignPlansAPI";
import { getAddressFuel } from "src/utils/addressFuel";
import { createSalesCart, createProspectMoveCart } from "src/utils/cartAPI";
import { getMovesCrossSellURL } from "src/utils/externalRoutes";
import { getCustomerType, getMoveCustomerType } from "src/utils/pageContext";
import { Disclaimer } from "src/components/Disclaimer";
import { ErgonPatchAlert } from "src/components/ErgonPatchAlert/ErgonPatchAlert";
import { BusinessAddressDialog } from "./BusinessAddressDialog";
import { LookupErrorAlert } from "./LookupErrorAlert";

const AddressLookupFieldset = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;

  button:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const PLAN_FUEL_BY_TYPE: Record<IFuelTypes, PlanFuelSelection> = {
  dual: FUEL_DUAL,
  electricity: FUEL_ELEC,
  gas: FUEL_GAS,
};

export const Lookup: React.FC<ILookup> = ({ ctaLabel, authenticated }) => {
  const [campaign, setCampaign] = useState<{
    addressId: string;
    plans: CampaignPlans;
    customerType: CustomerType;
  }>();
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(false);
  const [isBusinessDialogOpen, setIsBusinessDialogOpen] = useState(false);
  const planRequest = useRef(0);
  const submitting = useRef(false);
  const [initialAddressId] = useState(
    () =>
      new URLSearchParams(window.location.search).get("originAddressId") ||
      undefined
  );
  // The URL address is restored on mount; it is not a new customer selection.
  const addressToRestore = useRef(initialAddressId);
  const {
    selectedAddress,
    unconfirmedAddress,
    nmiResolutionState,
    setSelectedEnergy,
    isAddressLookupInProgress,
  } = useEnergyAddressLookup();
  const address = selectedAddress ?? unconfirmedAddress;
  const customerType = getMoveCustomerType(address?.customerClass);
  const isCes = nmiResolutionState === NMIResolutionState.CES;
  const isErgonAddress = !!address && isErgon(address);
  const isErgonSolarPatch = !!address && isErgonSolar(address);
  const resolutionComplete = [
    NMIResolutionState.Init,
    NMIResolutionState.Completed,
    NMIResolutionState.ExactMatchFound,
    NMIResolutionState.NmiHardStop,
  ].includes(nmiResolutionState);
  const campaignPlans =
    campaign?.addressId === address?.originAddressId &&
    campaign?.customerType === customerType
      ? campaign?.plans
      : undefined;
  // Only include electricity once the current address has a confirmed NMI.
  const plans = campaignPlans && {
    ...campaignPlans,
    electricity:
      address?.isExactMatchForNMI === true || address?.isSelected === true
        ? campaignPlans.electricity
        : undefined,
  };
  const emptyPlans = !!plans && !plans.electricity && !plans.gas;
  const addressFuel = address ? getAddressFuel(address) : undefined;
  const noAvailableEnergy = authenticated
    ? emptyPlans
    : !!address?.originAddressId && !addressFuel;
  const isReadyToSubmit =
    !!address?.originAddressId &&
    !isAddressLookupInProgress &&
    resolutionComplete &&
    !isCes &&
    (authenticated ? !!plans && !emptyPlans : !!addressFuel);

  const isResidentialToBusinessMove =
    getCustomerType() === "residential" && customerType === "business";
  const showBusinessAddressDialog =
    isResidentialToBusinessMove &&
    isReadyToSubmit &&
    // A hard stop can still have the existing meter-search dialog open.
    nmiResolutionState !== NMIResolutionState.NmiHardStop &&
    !isSubmitting &&
    isBusinessDialogOpen;

  const closeBusinessAddressDialog = () => setIsBusinessDialogOpen(false);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Browser Back restores the submission lock along with the cached page.
        submitting.current = false;
        setIsSubmitting(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    // Resolve both fuels before service selection on cross-sell, for every customer state.
    setSelectedEnergy({ electricity: true, gas: true });
    return () => {
      planRequest.current += 1;
    };
  }, [setSelectedEnergy]);

  useEffect(() => {
    if (!address || isAddressLookupInProgress) {
      setIsBusinessDialogOpen(false);
      planRequest.current += 1;
      setCampaign(undefined);
      setIsLoadingPlans(false);
      setGlobalError(false);
      removePlanSolar();
    }
  }, [address, isAddressLookupInProgress]);

  const handleAddressUpdate = async (nextAddress: ConnectionAddressObject) => {
    const isRestoringAddress =
      !!addressToRestore.current &&
      nextAddress.originAddressId === addressToRestore.current;
    if (!isRestoringAddress) addressToRestore.current = undefined;
    setIsBusinessDialogOpen(false);
    const request = ++planRequest.current;
    setGlobalError(false);
    setCampaign(undefined);
    removePlanSolar();
    const addressId = nextAddress.originAddressId;
    const nextCustomerType = getMoveCustomerType(nextAddress.customerClass);
    if (!authenticated || !addressId) {
      setIsBusinessDialogOpen(
        !isRestoringAddress &&
          !!addressId &&
          getCustomerType() === "residential" &&
          nextCustomerType === "business" &&
          !!getAddressFuel(nextAddress)
      );
      setIsLoadingPlans(false);
      return;
    }
    setIsLoadingPlans(true);

    const nextPlans = await getCampaignPlans(
      addressId,
      nextAddress.elecDetails?.hasSolarPanels || false,
      nextCustomerType
    );
    if (request === planRequest.current) {
      // New selections can show the notice; restoring the URL address cannot.
      setIsBusinessDialogOpen(
        !isRestoringAddress &&
          getCustomerType() === "residential" &&
          nextCustomerType === "business" &&
          !!(nextPlans.electricity || nextPlans.gas)
      );
      setCampaign({
        addressId,
        plans: nextPlans,
        customerType: nextCustomerType,
      });
      setIsLoadingPlans(false);
    }
  };

  const handleSubmit = async () => {
    if (
      submitting.current ||
      !isReadyToSubmit ||
      !address?.originAddressId ||
      (authenticated && !plans)
    )
      return;

    closeBusinessAddressDialog();
    submitting.current = true;
    setIsSubmitting(true);
    setGlobalError(false);
    setPlanSolar(address.elecDetails?.hasSolarPanels ? YesNoE.Yes : YesNoE.No);
    try {
      let fuel: IFuelTypes;
      if (authenticated) {
        const cartFuel = await createSalesCart(
          address.originAddressId,
          plans,
          customerType
        );
        if (!cartFuel) {
          throw new Error("No eligible energy plans in the saved cart");
        }
        fuel = cartFuel;
      } else {
        if (!addressFuel) {
          throw new Error("No available energy services at the address");
        }
        fuel = addressFuel;
        await createProspectMoveCart(address.originAddressId, customerType);
      }
      setPlanFuel(PLAN_FUEL_BY_TYPE[fuel]);
      window.location.href = getMovesCrossSellURL(
        address.originAddressId,
        authenticated ? fuel : undefined,
        // Address meters indicate requested services, not plan availability.
        // A strict fuel restriction would restore unavailable fuels on Back.
        authenticated
          ? undefined
          : {
              electricity: fuel === "electricity" || fuel === "dual",
              gas: fuel === "gas" || fuel === "dual",
              internet: customerType === "residential",
              lpg: customerType === "residential",
            }
      );
    } catch (error) {
      setGlobalError(true);
      logger.error("Failed to create sales cart", error);
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Stack data-id="movers-widget-wp" space="small">
      <BusinessAddressDialog
        open={showBusinessAddressDialog}
        onClose={closeBusinessAddressDialog}
        onContinue={handleSubmit}
      />
      <AddressLookupFieldset
        data-hj-suppress={true}
        aria-label="New connection address"
        onChangeCapture={(event) => {
          // Only editing the address input starts a fresh selection.
          if ((event.target as HTMLElement).id === "address-lookup") {
            addressToRestore.current = undefined;
          }
        }}
        aria-busy={isSubmitting}
        disabled={isSubmitting}
      >
        <UpliftedEnergyAddressLookup
          ariaLabel="New connection address"
          placeholder="Enter your new address..."
          ctaLabel={ctaLabel}
          ctaBlocked={!isReadyToSubmit || isSubmitting}
          ctaLoading={
            !isAddressLookupInProgress &&
            ((!!address &&
              nmiResolutionState === NMIResolutionState.InProgress) ||
              (authenticated && isLoadingPlans) ||
              isSubmitting)
          }
          originAddressId={initialAddressId}
          onAddressCleared={() => {
            addressToRestore.current = undefined;
            closeBusinessAddressDialog();
          }}
          onAddressSelected={handleAddressUpdate}
          onAddressUnconfirmed={handleAddressUpdate}
          onCtaClick={handleSubmit}
          searchByMirn={false}
        />
      </AddressLookupFieldset>
      {isSubmitting && (
        <Box
          display="flex"
          justifyContent="center"
          domProps={{ role: "status", "aria-label": "Saving your move" }}
        >
          <Preloader size="small" />
        </Box>
      )}
      <EnergyAddressLookup.BelowThresholdAlert />
      <EnergyAddressLookup.HardStopAlert />
      <EnergyAddressLookup.CesErrorAlert customerType={getCustomerType()} />
      {(globalError ||
        (noAvailableEnergy &&
          resolutionComplete &&
          !isAddressLookupInProgress &&
          !isCes &&
          !isErgonAddress)) && <LookupErrorAlert />}
      <ErgonPatchAlert
        isErgon={isErgonAddress}
        isErgonSolar={isErgonSolarPatch}
        selectedFuel={{ electricity: true, gas: true }}
        emptyPlans={noAvailableEnergy}
      />
      <Box paddingTop="medium">
        <Disclaimer />
      </Box>
    </Stack>
  );
};
